import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home, Search, Library, Compass, Music2, Settings, Menu, X, Crown, TrendingUp,
  DollarSign, Play, Disc3, ShieldCheck, Wand2, Globe, Award,
  Store, ShoppingCart
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

export const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, user, isAdmin } = useAuth();
  const { items, toggleCart } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path: string) => {
    if (path.includes('?')) {
      const [basePath, query] = path.split('?');
      return location.pathname === basePath && location.search.includes(query.split('=')[1]);
    }
    return location.pathname === path;
  };

  const mainLinks = [
    { icon: Home, label: 'Listen Now', path: '/' },
    { icon: Search, label: 'Browse', path: '/search' },
    { icon: Compass, label: 'Discover', path: '/discover' },
    { icon: TrendingUp, label: 'Charts', path: '/charts' },
  ];

  const fanLinks = user ? [
    { icon: Store, label: 'Marketplace', path: '/marketplace' },
    { icon: Award, label: 'Rewards', path: '/rewards' },
  ] : [];

  const libraryLinks = user ? [
    { icon: Library, label: 'Library', path: '/library' },
    { icon: Music2, label: 'Artists', path: '/artists' },
  ] : [];

  const creatorLinks = profile?.is_artist ? [
    { icon: Disc3, label: 'Vision Studio', path: '/creator-portal' },
    { icon: Store, label: 'Fan Store', path: '/creator-portal?tab=store' },
    { icon: Wand2, label: 'AI Studio', path: '/ai-studio' },
    { icon: Globe, label: 'Distribution', path: '/distribution' },
    { icon: DollarSign, label: 'Earnings', path: '/creator-portal?tab=earnings' },
  ] : [];

  const adminLinks = isAdmin ? [
    { icon: ShieldCheck, label: 'Admin Panel', path: '/admin' },
  ] : [];

  const NavLink = ({ icon: Icon, label, path }: { icon: React.ElementType; label: string; path: string }) => {
    const active = isActive(path);
    return (
      <Link
        to={path}
        onClick={() => setMobileMenuOpen(false)}
        className={`
          group relative flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 text-sm
          ${active
            ? 'text-royal-gold bg-royal-gold/5 font-medium'
            : 'text-royal-text-secondary hover:text-royal-text hover:bg-stone-50'
          }
        `}
      >
        {active && (
          <motion.div
            layoutId="activeIndicator"
            className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-royal-gold rounded-r-full"
            transition={{ type: 'spring', stiffness: 500, damping: 35 }}
          />
        )}
        <Icon size={18} strokeWidth={active ? 2.5 : 2} />
        <span>{label}</span>
      </Link>
    );
  };

  const SectionHeader = ({ children, gradient }: { children: React.ReactNode; gradient?: boolean }) => (
    <div className="px-4 pt-6 pb-2">
      <h2 className={`text-[11px] font-semibold uppercase tracking-wider ${
        gradient
          ? 'bg-gradient-to-r from-royal-gold-dark to-royal-gold bg-clip-text text-transparent'
          : 'text-royal-text-secondary/70'
      }`}>
        {children}
      </h2>
    </div>
  );

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-6 pb-4">
        <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-royal-gold to-royal-gold-dark flex items-center justify-center shadow-lg shadow-royal-gold/20">
              <Crown size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight text-royal-text">
                VISION
              </h1>
              <p className="text-[10px] text-royal-text-secondary font-medium tracking-wider -mt-0.5">MUSIC</p>
            </div>
          </div>
        </Link>
      </div>

      {user && (
        <div className="px-6 pb-2">
          <button
            onClick={toggleCart}
            className="relative flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-royal-text-secondary hover:text-royal-text hover:bg-stone-50 transition-all"
          >
            <ShoppingCart size={18} />
            <span>Cart</span>
            {items.length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-royal-gold text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {items.length}
              </span>
            )}
          </button>
        </div>
      )}

      <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
        {mainLinks.map((link) => (
          <NavLink key={link.path} {...link} />
        ))}

        {user && fanLinks.length > 0 && (
          <>
            <SectionHeader>Fan Economy</SectionHeader>
            {fanLinks.map((link) => (
              <NavLink key={link.path} {...link} />
            ))}
          </>
        )}

        {user && libraryLinks.length > 0 && (
          <>
            <SectionHeader>Your Library</SectionHeader>
            {libraryLinks.map((link) => (
              <NavLink key={link.path} {...link} />
            ))}
          </>
        )}

        {profile?.is_artist && creatorLinks.length > 0 && (
          <>
            <SectionHeader gradient>Creator Studio</SectionHeader>
            {creatorLinks.map((link) => (
              <NavLink key={link.path + link.label} {...link} />
            ))}
          </>
        )}

        {isAdmin && adminLinks.length > 0 && (
          <>
            <SectionHeader>Admin</SectionHeader>
            {adminLinks.map((link) => (
              <NavLink key={link.path} {...link} />
            ))}
          </>
        )}
      </nav>

      <div className="p-4 border-t border-stone-100">
        {user ? (
          <button
            onClick={() => {
              navigate('/settings');
              setMobileMenuOpen(false);
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-royal-text-secondary hover:text-royal-text hover:bg-stone-50 transition-all"
          >
            <Settings size={18} />
            <span>Settings</span>
          </button>
        ) : (
          <div className="space-y-2">
            <motion.button
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => {
                navigate('/signup');
                setMobileMenuOpen(false);
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-royal-gold text-white rounded-xl font-medium text-sm transition-all shadow-lg shadow-royal-gold/20 hover:bg-royal-gold-dark"
            >
              <Play size={16} fill="currentColor" />
              Start Free Trial
            </motion.button>
            <button
              onClick={() => {
                navigate('/login');
                setMobileMenuOpen(false);
              }}
              className="w-full text-center py-2 text-sm font-medium text-royal-text-secondary hover:text-royal-text transition-colors"
            >
              Sign In
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <button
        onClick={() => setMobileMenuOpen(true)}
        className="lg:hidden fixed top-5 left-5 z-50 p-3 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all border border-stone-100"
      >
        <Menu size={20} className="text-royal-text" />
      </button>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden fixed inset-0 bg-black/20 z-40 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      <aside
        className={`
          fixed top-0 left-0 h-full w-72 bg-white z-50 border-r border-stone-100
          transform transition-transform duration-300 ease-out
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <button
          onClick={() => setMobileMenuOpen(false)}
          className="lg:hidden absolute top-5 right-5 p-2 hover:bg-stone-50 rounded-lg transition-colors"
        >
          <X size={20} />
        </button>
        <SidebarContent />
      </aside>
    </>
  );
};
